import { request } from "@/api/client";

export type ResolvedArtist = {
  id: number;
  name: string;
  spotify_id: string;
  bandsintown_slug?: string | null;
  tracker_count?: number | null;
  genre?: string | null;
  source: string;
  source_playlist?: string | null;
  crm_contact_id?: number | null;
  imported_at?: string | null;
  created_at: string;
};

type ResolvedArtistListResponse = {
  artists: ResolvedArtist[];
  total: number;
};

export type ArtistIngestResponse = {
  artist_id: number;
  bandsintown_slug?: string | null;
  inserted_events?: number | null;
  venues_touched?: number | null;
  errors?: string[];
  crm_contacts_created?: number | null;
  crm_contacts_updated?: number | null;
  crm_contacts_failed?: number | null;
};

export function fetchResolvedArtists(
  params: { limit?: number; offset?: number; imported?: boolean | null; playlist?: string | null } = {},
) {
  const search = new URLSearchParams();
  if (typeof params.limit === "number") search.set("limit", String(params.limit));
  if (typeof params.offset === "number") search.set("offset", String(params.offset));
  if (params.imported === true) search.set("imported", "true");
  if (params.imported === false) search.set("imported", "false");
  if (params.playlist) search.set("playlist", params.playlist);
  const suffix = search.toString();
  return request<ResolvedArtistListResponse>(`/artist-resolver/resolved-artists${suffix ? `?${suffix}` : ""}`);
}

export function importResolvedArtist(artistId: number) {
  return request<{ artist_id: number; crm_contact_id: number; status: "created" | "updated" }>(
    `/artist-resolver/resolved-artists/${encodeURIComponent(artistId)}/import`,
    {
      method: "POST",
    },
  );
}

export function bootstrapPlaylist(payload: {
  playlist: string;
  follower_max?: number | null;
  follower_min?: number | null;
}) {
  return request<{ status: string; task_id: string }>(`/artist-resolver/bootstrap/playlist`, {
    method: "POST",
    body: payload,
  });
}

export function bootstrapGenre(genre: string) {
  return request<{ status: string; task_id: string }>(`/artist-resolver/bootstrap/genre`, {
    method: "POST",
    body: { genre },
  });
}

export function previewPlaylist(params: {
  playlist: string;
  min_followers?: number;
  max_followers?: number;
  limit?: number;
  offset?: number;
}) {
  const search = new URLSearchParams();
  search.set("playlist", params.playlist);
  if (typeof params.min_followers === "number") search.set("min_followers", String(params.min_followers));
  if (typeof params.max_followers === "number") search.set("max_followers", String(params.max_followers));
  if (typeof params.limit === "number") search.set("limit", String(params.limit));
  if (typeof params.offset === "number") search.set("offset", String(params.offset));
  return request<{
    playlist: string;
    artists: { name: string; spotify_id: string; followers: number; genres: string[] }[];
    offset: number;
    limit: number;
    min_followers: number;
    max_followers?: number | null;
    has_more: boolean;
  }>(`/artist-resolver/playlist-preview?${search.toString()}`);
}

export function ingestResolvedArtist(artistId: number) {
  return request<ArtistIngestResponse>(`/artist-resolver/resolved-artists/${encodeURIComponent(artistId)}/ingest`, {
    method: "POST",
  });
}

export function queueSimilarResolvedArtist(artistId: number) {
  return request<{ status: string }>(`/artist-resolver/resolved-artists/${encodeURIComponent(artistId)}/similar`, {
    method: "POST",
  });
}

export function deleteResolvedArtist(artistId: number) {
  return request<void>(`/artist-resolver/resolved-artists/${encodeURIComponent(artistId)}`, {
    method: "DELETE",
  });
}
